import { useEffect, useRef } from 'react';
import './video-browser.scss';
import VideoBrowserAd from '../adsterra/video-browser-ad';

interface VideoBrowserProps {
  videoHistory: Array<Array<VideoInfo>>;
  roundNumber?: number;
}

/**
 * A video card within the VideoBrowser component that displays the video info and thumbnail.
 * @param {VideoInfo} video- Information about the video to display.
 * @returns {JSX.Element} - A JSX element representing the video card.
 */
function VideoCard({ video }: { video: VideoInfo }): JSX.Element {
  return (
    <div className="video-card" key={`${video.prompt}${video.videoId}`}>
      <img className="thumbnail" src={video.thumbnailURL} alt={video.title} />
      <div className="video-info">
        <div className="video-title">
          <a href={`https://www.youtube.com/watch?v=${video.videoId}`} target="_blank">{video.title}</a>
        </div>
        <div className="channel">
          By <a href={`https://www.youtube.com/channel/${video.channelId}`} target="_blank">{video.channelName}</a>
        </div>
        <div className="description">{video.description}</div>
        <div className="release-date">Released {video.releaseDate}</div>
        <div className="views">Views: {video.views.toLocaleString('en-US')}</div>
      </div>
    </div>
  );
}

/**
 * A sticky header for a round's video history that displays the search prompt and optionally the round number.
 * @param {string} prompt - The search prompt for the round.
 * @param {number} videoRoundNumber - (optional) The round number to display.
 * @returns {JSX.Element} - A JSX element representing the round header.
 */
function RoundHeader({ prompt, videoRoundNumber }: { prompt: string; videoRoundNumber?: number }): JSX.Element {
  return (
    <div className="section-header" key={`${prompt}${videoRoundNumber}`}>
      <div className="search">Search: <span className="prompt">{prompt}</span></div>
      {videoRoundNumber!==undefined &&
      <div className="round-number">
        Round {videoRoundNumber + 1}
      </div>
      }
    </div>
  );
}

/**
 * A component that displays either a specific round's video history, or the entire game's video history
 * @param {Array<Array<VideoInfo>>} videoHistory - Info for all of the videos the user has watched, organized by round.
 * @param {number} roundNumber - (Optional) The round number to show history for, pass if you only want to show one round.
 */
const VideoBrowser: React.FC<VideoBrowserProps> = ({ videoHistory, roundNumber }) => {
  const browserRef = useRef<null | HTMLDivElement>(null);
  const historyLength = roundNumber ? videoHistory[roundNumber].length : videoHistory.reduce((currentCount, row) => currentCount + row.length, 0);

  useEffect(()=> {
    /*Automatically scrolls to the bottom to show the video history and serve the ad at the bottom. The scroll speed will vary based on the amount of videos and scroll at ~60fps*/
    const betterScrollTo = async () => {
      if (browserRef.current?.scrollTop !== undefined) {
        const interval = setInterval(() => {
          browserRef.current?.scrollTo(0, browserRef.current?.scrollTop + historyLength*.6 + 1);
          if (browserRef.current?.scrollTop !== undefined && browserRef.current?.scrollTop > browserRef.current?.scrollHeight - browserRef.current?.offsetHeight - 5) {
            clearInterval(interval);
          }
        }, 12)
      }
    }
    setTimeout(betterScrollTo, 650);
  }, [browserRef]);

  /**
   * Generates video cards and headers for a specific round's videos.
   * @param {VideoInfo[]} roundVideos - An array of videos for the round.
   * @param {number} [videoRoundNumber] - (optional) The round number to display.
   * @returns {JSX.Element[]} - An array of JSX elements representing video cards.
   */
  function generateRoundVideoCards(roundVideos: VideoInfo[], videoRoundNumber?: number): JSX.Element[] {
    let prompt: string;
    let returnElements: JSX.Element[] = [];
    roundVideos?.forEach((video, i) => {
      if (video.prompt !== prompt) {
        prompt = video.prompt;
        returnElements.push(<RoundHeader key={`header-${prompt}`} prompt={prompt} videoRoundNumber={videoRoundNumber} />)
      }
      returnElements.push(<VideoCard key={video.videoId + i} video={video} />)
    });
    returnElements.push(<br key={`round${videoRoundNumber}break`}></br>);
    return returnElements;
  }

  const videoCards = roundNumber !== undefined
    ? generateRoundVideoCards(videoHistory[roundNumber])
    : videoHistory.flatMap((roundVideos, roundNumber) => generateRoundVideoCards(roundVideos, roundNumber));

  return (
    <div ref={browserRef} className="video-browser">
      {videoCards}
      {/* <div key={'ad-header'} className="section-header">
        <div className="search">Advertisement</div>
      </div>
      <div key={'ad-card'} className="video-card">
        <VideoBrowserAd/>
      </div> */}
    </div>
  )
}

export default VideoBrowser;